import { ref, computed } from "vue";
import { createLatestRequestScope } from "@/composables/useLatestRequest";
import { getPack } from "../api";
import type { PackWithUi, RunAgentTask } from "../types";
import { useRunAgentPlatforms } from "./useRunAgentPlatforms";
import { useRunAgentUserInput } from "./useRunAgentUserInput";

export interface ScheduledPackSelection {
	pack: PackWithUi;
	taskIds: string[];
	userInputData?: Record<string, Record<string, any>>;
}

export function useScheduledPackInput() {
	const loadRequests = createLatestRequestScope();
	const selectedPack = ref<PackWithUi | null>(null);
	const loadedPackTasks = ref<RunAgentTask[]>([]);
	const loadingPack = ref(false);
	const error = ref<string | null>(null);
	const selectedTaskIds = ref<string[]>([]);

	// Dummy refs for RunAgentOrchestrator compatibility
	const runningAnalysis = ref(false);
	const pendingMetadata = ref(null);
	const reportNameDraft = ref("");
	const pendingAssets = ref(null);
	const mediaWarnings = computed(() => [] as string[]);
	const hasNonReadyMedia = computed(() => false);
	const usingSelectedMedia = computed(() => false);
	const totalMediaCount = ref(0);
	const readyMediaCount = ref(0);
	const selectedMediaCount = ref(0);

	const platformComposable = useRunAgentPlatforms({ loadedPackTasks });
	const {
		hasPlatformRequirements,
		allPlatformsAuthenticated,
		computePlatformRequirements,
		loadPlatformsIfNeeded,
		reset: resetPlatforms,
	} = platformComposable;

	const userInputComposable = useRunAgentUserInput({ loadedPackTasks });
	const {
		hasUserInputItems,
		userInputData,
		fetchUserInputConfigsForPack,
		reset: resetUserInput,
	} = userInputComposable;

	const needsDialog = computed(() => {
		return (hasPlatformRequirements.value && !allPlatformsAuthenticated.value) || hasUserInputItems.value;
	});

	const loadPackDetails = async (pack: PackWithUi) => {
		const { requestId } = loadRequests.startRequest();
		resetPlatforms();
		selectedPack.value = pack;
		error.value = null;
		loadingPack.value = true;

		try {
			const [packDetail] = await Promise.all([getPack(pack.id), loadPlatformsIfNeeded()]);
			if (loadRequests.isSuperseded(requestId)) return;

			loadedPackTasks.value = (packDetail as any).pack?.tasks || [];
			computePlatformRequirements(loadedPackTasks.value);
			await fetchUserInputConfigsForPack(pack.id, undefined, undefined, undefined, () => loadRequests.isSuperseded(requestId));
			if (loadRequests.isSuperseded(requestId)) return;

			selectedTaskIds.value = [...new Set(loadedPackTasks.value.map((task) => task.id))];
		} catch (exception: any) {
			if (loadRequests.isSuperseded(requestId)) return;
			console.error("Failed to load pack details:", exception);
			error.value = exception?.message || "Failed to load pack details.";
			selectedPack.value = null;
		} finally {
			if (!loadRequests.isSuperseded(requestId)) loadingPack.value = false;
		}
	};

	const getCollectedData = (): ScheduledPackSelection | null => {
		if (!selectedPack.value) {
			return null;
		}

		const finalUserInputData: Record<string, Record<string, any>> = {};
		for (const [key, data] of Object.entries(userInputData.value)) {
			if (Object.keys(data).length > 0) {
				finalUserInputData[key] = data;
			}
		}

		return {
			pack: selectedPack.value,
			taskIds: [...new Set(selectedTaskIds.value)],
			userInputData: Object.keys(finalUserInputData).length > 0 ? finalUserInputData : undefined,
		};
	};

	const reset = () => {
		loadRequests.startRequest();
		selectedPack.value = null;
		loadedPackTasks.value = [];
		loadingPack.value = false;
		error.value = null;
		selectedTaskIds.value = [];
		resetPlatforms();
		resetUserInput();
	};

	// Dummy runAnalysis for RunAgentOrchestrator compatibility (never called in collect mode)
	const runAnalysis = async (): Promise<boolean> => false;

	return {
		selectedPack,
		loadedPackTasks,
		loadingPack,
		error,
		selectedTaskIds,
		runningAnalysis,
		pendingMetadata,
		reportNameDraft,
		pendingAssets,
		mediaWarnings,
		hasNonReadyMedia,
		usingSelectedMedia,
		totalMediaCount,
		readyMediaCount,
		selectedMediaCount,
		needsDialog,
		platformComposable,
		userInputComposable,
		loadPackDetails,
		getCollectedData,
		runAnalysis,
		reset,
	};
}

export type ScheduledPackInputOrchestrator = ReturnType<typeof useScheduledPackInput>;
